import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddchartIcon from '@mui/icons-material/Addchart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BlockIcon from '@mui/icons-material/Block';
import BoltIcon from '@mui/icons-material/Bolt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { Divider, ListItemIcon, Menu, MenuItem } from '@mui/material';
import { ThemeProvider, type Theme } from '@mui/material/styles';
import GuideButton from '../common/guide/GuideButton';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import aiChatService, {
  AiChatActionCallDto,
  AiChatConversationSummary,
  AiChatReportSpecDto,
  AiChatSendResult,
} from '../../services/aiChatService';
import { getActionExecutor } from './agent/actionExecutors';
import aiPlanService, {
  AI_PLAN_CHANGED_EVENT,
  AiPlanDetail,
  AiPlanTaskDto,
} from '../../services/aiPlanService';
import AiPlanPanel from './plan/AiPlanPanel';
import { buildContinueTaskPrompt, buildTaskPrompt, isManualCompletionTask, isPlanTaskPrompt } from './plan/planPromptBuilder';
import {
  ReportSpec,
  ReportTimeGranularity,
  ReportVisual,
  createReportId,
} from '../accounting/dashboard/reportCatalog';
import { upsertCustomReport } from '../accounting/dashboard/customReportsStore';
import DynamicReportCard from '../accounting/dashboard/components/DynamicReportCard';

/**
 * Kullanıcının seçebileceği modeller — id backend whitelist'iyle (AiChatManager.SelectableModels)
 * birebir aynı olmalı; geçersiz id sunucuda sessizce varsayılana düşer.
 * proOnly: yalnızca AI Pro (agentEnabled) şirketler seçebilir; kotadan 5 kat token düşer.
 */
const SELECTABLE_MODELS: Array<{ id: string; label: string; proOnly?: boolean; quotaNote?: string }> = [
  { id: 'gemini-flash', label: 'Gemini Flash' },
  { id: 'gemini-3.5', label: 'Gemini 3.5' },
  { id: 'gemini-pro', label: 'Gemini Pro', proOnly: true, quotaNote: '5× token' },
];

/** Henüz kullanılamayan modeller — menüde "Çok Yakında" başlığı altında pasif listelenir. */
const COMING_SOON_MODELS = [
  'ChatGPT',
  'Opus 4.8',
  'Sonnet 4.6',
  'Cursor Grok 4.5',
  'Composer 2.5',
  'GPT-5.6 Sol',
  'GPT-5.6 Terra',
  'Fable 5',
];

const MODEL_STORAGE_KEY = 'aiChatSelectedModel';

/** (i) rehberi: sohbet penceresi zIndex 2100 olduğundan rehber penceresi ve ipucu onun üstüne alınır. */
const raiseGuideLayers = (outer: Theme): Theme => ({
  ...outer,
  zIndex: { ...outer.zIndex, modal: 2300, tooltip: 2400 },
});

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  reportSpec?: AiChatReportSpecDto | null;
  /** Rapor bu mesajdan dashboard'a eklendi mi (buton durumu) */
  reportAdded?: boolean;
  isError?: boolean;
  /** Paketi olmayan şirkette gönderim sonrası paket tanıtım mesajı */
  isUpsell?: boolean;
  /** Ajan modu: bu mesaja bağlı eylem çağrısı (onay kartı / durum göstergesi) */
  actionCall?: AiChatActionCallDto | null;
  /** pending = onay bekliyor (write), running = yürütülüyor, done/cancelled/failed = sonuç */
  actionStatus?: 'pending' | 'running' | 'done' | 'cancelled' | 'failed';
  /** Geçici hata (yoğunluk vb.) — "Devam Et" butonu ile kaldığı yerden sürdürülebilir */
  canRetry?: boolean;
}

interface AiChatWindowProps {
  onClose: () => void;
  remainingTokens: number;
  onRemainingTokensChange: (remaining: number) => void;
  /** Şirketin AI paketi aktif mi? False ise gönderim paket tanıtımına yönlendirir. */
  packageActive: boolean;
  /** AI Pro: ajan modu izni (SuperAdmin yönetir). Ajan modu UI'da şimdilik kilitli. */
  agentEnabled: boolean;
  /** AI Pro: plan modu izni — çok adımlı görev planları paneli (SuperAdmin yönetir). */
  planEnabled: boolean;
}

/** Satış hattı — paket tanıtımında gösterilir */
const SALES_PHONE_DISPLAY = '+90 (312) 400 09 12';
const SALES_PHONE_TEL = '+903124000912';

/** **kalın** işaretlerini <strong>'a çevirir; diğer metin aynen kalır. */
const renderInline = (text: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith('**') && seg.endsWith('**') ? (
      <strong key={i}>{seg.slice(2, -2)}</strong>
    ) : (
      seg
    )
  );

/**
 * Asistan mesajındaki hafif markdown'ı render eder: görseller (![alt](url))
 * gerçek resim olarak, ###/## başlıklar kalın satır olarak, **bold** desteklenir.
 * Görsel URL'leri site köküne göredir (/ai-docs/img/...) — public/ altından servis edilir.
 * Görsele tıklanınca yeni sekme yerine uygulama içi önizleme modalı açılır.
 */
const renderAssistantContent = (
  content: string,
  onImageClick: (src: string, alt: string) => void
): React.ReactNode => {
  const imageRegex = /!\[([^\]]*)\]\(([^)\s]+)\)/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  const pushText = (text: string) => {
    const trimmed = text.replace(/^\n+|\n+$/g, '');
    if (!trimmed) return;
    nodes.push(
      <Typography
        key={`t_${key++}`}
        variant="body2"
        component="div"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {trimmed.split('\n').map((line, i) => {
          const heading = line.match(/^#{1,4}\s+(.*)$/);
          return (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {heading ? <strong>{renderInline(heading[1])}</strong> : renderInline(line)}
            </React.Fragment>
          );
        })}
      </Typography>
    );
  };

  while ((match = imageRegex.exec(content)) !== null) {
    pushText(content.slice(lastIndex, match.index));
    const [fullMatch, alt, src] = match;
    nodes.push(
      <Box
        key={`img_${key++}`}
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        onClick={() => onImageClick(src, alt)}
        sx={{
          display: 'block',
          maxWidth: '100%',
          my: 0.75,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'zoom-in',
        }}
      />
    );
    lastIndex = match.index + fullMatch.length;
  }
  pushText(content.slice(lastIndex));

  return nodes.length > 0 ? nodes : content;
};

const toReportSpec = (dto: AiChatReportSpecDto): ReportSpec => ({
  id: createReportId(),
  title: dto.title,
  dataSource: dto.dataSource,
  metric: dto.metric,
  dimension: dto.dimension,
  timeGranularity:
    dto.dimension === 'time'
      ? ((dto.timeGranularity ?? 'day') as ReportTimeGranularity)
      : undefined,
  filters: dto.filters,
  visual: dto.visual as ReportVisual,
  limit: dto.limit,
});

/**
 * AI'nın çözdüğü "yyyy-MM-dd" tarihlerini yerel gün sınırlarına (00:00 – 23:59:59.999)
 * çevirir. Spec'te tarih yoksa null döner — çağıran taraf varsayılan aralığı kullanır.
 */
const resolveSpecDateRange = (
  dto: AiChatReportSpecDto
): { startDate: string; endDate: string } | null => {
  if (!dto.startDate || !dto.endDate) return null;
  const parse = (s: string, endOfDay: boolean): Date | null => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const d = endOfDay
      ? new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59, 999)
      : new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  };
  const start = parse(dto.startDate, false);
  const end = parse(dto.endDate, true);
  if (!start || !end || end < start) return null;
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

/** Tarih aralığını kısa etikete çevirir: tek gün "19.07.2026", aralık "01.07.2026 – 19.07.2026". */
const formatSpecDateRange = (dto: AiChatReportSpecDto): string | null => {
  if (!dto.startDate || !dto.endDate) return null;
  const fmt = (s: string) => {
    const [y, m, d] = s.split('-');
    return `${d}.${m}.${y}`;
  };
  return dto.startDate === dto.endDate
    ? fmt(dto.startDate)
    : `${fmt(dto.startDate)} – ${fmt(dto.endDate)}`;
};

/**
 * Sağ altta açılan AI chat paneli: mesajlaşma, sunucu tarafı konuşma geçmişi,
 * rapor önerisi önizlemesi + "Dashboard'a ekle". Kota dolduğunda giriş kilitlenir.
 */
const AiChatWindow: React.FC<AiChatWindowProps> = ({
  onClose,
  remainingTokens,
  onRemainingTokensChange,
  packageActive,
  agentEnabled,
  planEnabled,
}) => {
  const { t } = useTranslation('common');
  const { currentUser, userDetails, branches } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 599px)');

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | undefined>(undefined);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(remainingTokens <= 0);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<AiChatConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  /** Mesajdaki görsele tıklanınca açılan uygulama içi önizleme */
  const [previewImage, setPreviewImage] = useState<{ src: string; alt: string } | null>(null);
  /** "Büyüt" ile modalda açılan rapor */
  const [expandedReport, setExpandedReport] = useState<AiChatReportSpecDto | null>(null);
  /** AI Standart / AI Pro paket tanıtım modalı */
  const [packagesOpen, setPackagesOpen] = useState(false);
  /**
   * Soru/Ajan/Plan modu — ajan modu agentEnabled, plan modu planEnabled (AI Pro)
   * şirketlerde seçilebilir. Plan modunda AI kullanıcıyla konuşarak plan kurar.
   */
  const [chatMode, setChatMode] = useState<'ask' | 'agent' | 'plan'>('ask');
  /**
   * Aktif gönderimin ÇÖZÜLMÜŞ modu — eylem zincirinin devam turları (action-result)
   * aynı modla sürsün diye ref'te tutulur (state asenkron olduğundan stale closure önlemi).
   */
  const activeModeRef = useRef<'ask' | 'agent' | 'plan'>('ask');
  // Model seçimi kalıcıdır (localStorage); bilinmeyen kayıtlı değer varsayılana düşer
  const [chatModel, setChatModel] = useState<string>(() => {
    const saved = localStorage.getItem(MODEL_STORAGE_KEY);
    return SELECTABLE_MODELS.some((m) => m.id === saved) ? (saved as string) : SELECTABLE_MODELS[0].id;
  });
  const [modeMenuAnchor, setModeMenuAnchor] = useState<HTMLElement | null>(null);
  const [modelMenuAnchor, setModelMenuAnchor] = useState<HTMLElement | null>(null);

  const selectModel = (id: string) => {
    setChatModel(id);
    localStorage.setItem(MODEL_STORAGE_KEY, id);
    setModelMenuAnchor(null);
  };

  // AI Pro olmayan şirkette kayıtlı seçim Pro ise varsayılana döndür
  // (ör. paket kapatıldı ya da localStorage elle değiştirildi)
  useEffect(() => {
    const def = SELECTABLE_MODELS.find((m) => m.id === chatModel);
    if (def?.proOnly && !agentEnabled) selectModel(SELECTABLE_MODELS[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentEnabled, chatModel]);
  /** Kullanıcı mesajı başına otomatik yürütülen eylem sayısı (sonsuz zincir emniyeti) */
  const actionChainRef = useRef(0);

  // ===== Plan Modu (AI Pro) =====
  /** Plan paneli açık mı (chat penceresinin solunda kardeş Paper) */
  const [planPanelOpen, setPlanPanelOpen] = useState(false);
  /** Görev durumu değişince artar — panel açık plan detayını tazeler */
  const [planRefreshKey, setPlanRefreshKey] = useState(0);
  /** Şu anda çalıştırılan plan görevi (panel spinner'ı için state, async akış için ref) */
  const [activePlanTaskId, setActivePlanTaskId] = useState<number | null>(null);
  /**
   * completeAs: zincir başarıyla bitince yazılacak durum. Yarı otomatik görevlerde
   * ([ELLE-TAMAMLANIR] işaretli — işi kullanıcı yapar) 'needsReview', normalde 'done'.
   */
  const activePlanTaskRef = useRef<{
    planId: number;
    taskId: number;
    completeAs: 'done' | 'needsReview';
  } | null>(null);
  /**
   * Aktif görev zincirinde en az bir eylem başarıyla çalıştı mı?
   * Zincir metinle bitince: true → görev done; false → AI soru sordu, görev running kalır.
   */
  const planChainActionOkRef = useRef(false);

  // AI plan eylemleri (createAiPlan vb.) plan verisini değiştirince paneli tazele;
  // yeni plan oluşturulduysa (openPanel) panel otomatik açılır.
  useEffect(() => {
    const onPlanChanged = (e: Event) => {
      setPlanRefreshKey((k) => k + 1);
      if ((e as CustomEvent).detail?.openPanel) setPlanPanelOpen(true);
    };
    window.addEventListener(AI_PLAN_CHANGED_EVENT, onPlanChanged);
    return () => window.removeEventListener(AI_PLAN_CHANGED_EVENT, onPlanChanged);
  }, []);

  /**
   * "Tümünü Onayla" modu: aktifken görev bitene kadar (model metinle cevap verene,
   * kullanıcı yeni mesaj yazana ya da bir eylemi reddedene kadar) sonraki yazma
   * eylemleri onay kartı beklemeden otomatik yürütülür — Cursor'daki toplu onay gibi.
   */
  const autoApproveRef = useRef(false);

  /**
   * "Tümünü yap" (plan): aktif plan id'si. Görev 'done' bitince sıradaki görev
   * otomatik başlatılır; failed/needsReview/durdurma/elle mesajda akış durur.
   */
  const runAllRef = useRef<number | null>(null);
  /** Görev bitişinde artar — sending=false olunca effect sıradaki görevi başlatır. */
  const [runAllTick, setRunAllTick] = useState(0);

  /** Durdur butonu: bekleyen istek iptali + zincirin sonraki adımlarının engellenmesi. */
  const abortRef = useRef<AbortController | null>(null);
  const stopRequestedRef = useRef(false);

  /** Axios iptal hatası mı? (Durdur butonu — hata mesajı yerine "durduruldu" akışı) */
  const isAbortError = (err: any): boolean =>
    err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.message === 'canceled';

  /**
   * Son geçici hatada (AI yoğun vb.) yarım kalan isteği yeniden deneyen kapanış.
   * Backend, Gemini çağrısı BAŞARILI olmadan hiçbir şeyi kaydetmediği için aynı
   * isteği tekrar göndermek güvenlidir (çift kayıt oluşmaz) — "Devam Et" butonu bunu çağırır.
   */
  const retryOpRef = useRef<(() => Promise<void>) | null>(null);

  const handleImageClick = useCallback((src: string, alt: string) => {
    setPreviewImage({ src, alt });
  }, []);

  // Sol kenardan sürükleyerek genişletme (yalnızca masaüstü).
  // Varsayılan genişlik alt sınırdır — pencere daraltılamaz, genişletilip geri getirilebilir.
  const DEFAULT_WIDTH = 380;
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [resizing, setResizing] = useState(false);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (isMobile) return;
      e.preventDefault();
      setResizing(true);
      const startX = e.clientX;
      const startWidth = width;
      const maxWidth = Math.min(900, window.innerWidth - 48);

      const onMove = (ev: PointerEvent) => {
        // Sola sürükleyince (clientX küçülür) pencere genişler
        const next = startWidth + (startX - ev.clientX);
        setWidth(Math.min(maxWidth, Math.max(DEFAULT_WIDTH, next)));
      };
      const onUp = () => {
        setResizing(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [isMobile, width]
  );

  const listRef = useRef<HTMLDivElement | null>(null);

  const userId = currentUser?.uid || '';
  const userEmail = currentUser?.email || null;

  // Rapor önizlemesi için son 30 gün (dashboard'a eklendiğinde kart dashboard filtresini kullanır)
  const previewRange = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }, []);

  const branchList = useMemo(
    () =>
      (branches || [])
        .filter((b: any) => (b.id || b.branchId) && b.name)
        .map((b: any) => ({ id: (b.id || b.branchId) as string, name: b.name as string })),
    [branches]
  );

  // Yeni mesajda en alta kaydır
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, sending]);

  /** Başarısız durum kodunu kullanıcı dostu UYARI mesajına çevirir (kırmızı hata değil). */
  const warningKeyForCode = (code: string | null): string =>
    code === 'QUOTA_EXCEEDED'
      ? 'aiChat.quotaExceeded'
      : code === 'AI_DISABLED'
        ? 'aiChat.disabled'
        : code === 'AGENT_DISABLED'
          ? 'aiChat.agentDisabled'
          : code === 'PLAN_DISABLED'
            ? 'aiChat.planDisabledMsg'
            : code === 'RATE_LIMIT'
              ? 'aiChat.rateLimit'
              : code === 'AI_BUSY'
                ? 'aiChat.busy'
                : 'aiChat.error';

  /** Geçici (yeniden denenebilir) sağlayıcı hataları — "Devam Et" butonu gösterilir. */
  const isRetryableCode = (code: string | null): boolean =>
    code === 'AI_BUSY' || code === 'RATE_LIMIT' || code === 'AI_ERROR';

  /**
   * Aktif plan görevini sonlandırır: durum sunucuya yazılır, plana konuşma id'si
   * bağlanır (başka cihazdan devam için) ve panel tazelenir.
   */
  const finishPlanTask = async (
    status: 'done' | 'failed' | 'needsReview',
    resultSummary?: string,
    convId?: number
  ) => {
    const active = activePlanTaskRef.current;
    if (!active) return;
    activePlanTaskRef.current = null;
    setActivePlanTaskId(null);
    try {
      await aiPlanService.updateTask(active.taskId, userId, status, resultSummary ?? null);
      const conv = convId ?? conversationId;
      if (conv) {
        // Görevler arası süreklilik: plana bağlı konuşma kaydedilir (fire-and-forget)
        aiPlanService.updatePlan(active.planId, userId, { conversationId: conv }).catch(() => {});
      }
    } catch (err) {
      console.error('[AiChatWindow] plan görev durumu yazılamadı:', err);
    }
    setPlanRefreshKey((k) => k + 1);

    // "Tümünü yap" zinciri: görev başarıyla bittiyse sıradaki görev tetiklenir
    // (effect, zincir tamamen çözülüp sending=false olunca başlatır);
    // failed/needsReview'da otomatik akış durur — kullanıcı panelden karar verir.
    if (runAllRef.current === active.planId) {
      if (status === 'done') setRunAllTick((k) => k + 1);
      else runAllRef.current = null;
    }
  };

  /**
   * Durdurma sonrası ortak temizlik (Cursor'daki Stop gibi): bekleyen/çalışan eylem
   * kartları iptal edilir, aktif plan görevi 'needsReview' olur, toplu akışlar kapanır
   * ve kısa bir "durduruldu" notu eklenir. restoreText verilirse durdurulan kullanıcı
   * mesajı giriş kutusuna geri konur — düzenlenip yeniden gönderilebilir.
   */
  const applyStopCleanup = (removeMessageId?: string, restoreText?: string) => {
    stopRequestedRef.current = false;
    runAllRef.current = null;
    autoApproveRef.current = false;
    retryOpRef.current = null;
    if (activePlanTaskRef.current) void finishPlanTask('needsReview');
    setMessages((prev) => [
      ...prev
        .filter((m) => m.id !== removeMessageId)
        .map((m) =>
          m.actionStatus === 'pending' || m.actionStatus === 'running'
            ? { ...m, actionStatus: 'cancelled' as const }
            : m
        ),
      { id: `a_${Date.now()}`, role: 'assistant', content: t('aiChat.stopped') },
    ]);
    if (restoreText) setText((cur) => (cur.trim() ? cur : restoreText));
  };

  /** Durdur butonu: bekleyen istek iptal edilir, zincirin sonraki adımları başlatılmaz. */
  const handleStop = () => {
    stopRequestedRef.current = true;
    abortRef.current?.abort();
  };

  /**
   * Hata mesajı ekler; retry verilirse "Devam Et" butonu gösterilir.
   * Önceki mesajlardaki retry butonları kapatılır (yalnızca son hata sürdürülebilir).
   * Aktif plan görevi varsa 'failed' işaretlenir — panelde "Devam et" aktifleşir.
   */
  const pushFailure = (contentKey: string, retry?: () => Promise<void>) => {
    retryOpRef.current = retry ?? null;
    if (activePlanTaskRef.current) void finishPlanTask('failed');
    setMessages((prev) => [
      ...prev.map((m) => (m.canRetry ? { ...m, canRetry: false } : m)),
      {
        id: `a_${Date.now()}`,
        role: 'assistant',
        content: t(contentKey),
        isError: true,
        canRetry: !!retry,
      },
    ]);
  };

  /** "Devam Et": yarım kalan isteği aynen tekrar gönderir (kaldığı yerden sürer). */
  const handleRetry = async (messageId: string) => {
    const retry = retryOpRef.current;
    if (!retry || sending) return;
    retryOpRef.current = null;
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, canRetry: false } : m)));
    await retry();
  };

  /**
   * Gemini turunun ortak işlenişi (mesaj gönderme + eylem sonucu devam turu):
   * metin yanıtı akışa eklenir; actionCall varsa read otomatik yürütülür,
   * write onay kartı olarak bekletilir ("Tümünü Onayla" aktifse otomatik yürütülür).
   * retryOp: bu sonucu üreten isteğin aynı parametrelerle tekrar gönderilebilir hali —
   * geçici hatalarda "Devam Et" butonuna bağlanır.
   */
  const processSendResult = async (result: AiChatSendResult, retryOp?: () => Promise<void>) => {
    if (!result.success) {
      if (result.code === 'QUOTA_EXCEEDED') {
        setQuotaExceeded(true);
        onRemainingTokensChange(0);
      }
      pushFailure(
        warningKeyForCode(result.code),
        retryOp && isRetryableCode(result.code) ? retryOp : undefined
      );
      return;
    }

    setConversationId(result.conversationId);
    onRemainingTokensChange(result.remainingTokens);
    if (result.remainingTokens <= 0) setQuotaExceeded(true);

    const action = result.actionCall;
    if (!action) {
      autoApproveRef.current = false; // görev bitti — toplu onay modu kapanır
      setMessages((prev) => [
        ...prev,
        {
          id: `a_${Date.now()}`,
          role: 'assistant',
          content: result.reply,
          reportSpec: result.reportSpec,
        },
      ]);
      // Plan görevi: zincir metinle bitti. En az bir eylem başarıyla çalıştıysa görev
      // tamamlandı (son cevabın özeti sonraki adımlara bağlam olur); hiç eylem çalışmadıysa
      // AI soru sormuştur — görev running kalır, kullanıcı chat'te cevaplayınca akış sürer.
      // Yarı otomatik görevler ([ELLE-TAMAMLANIR]) eylem koşulu aranmadan 'needsReview' olur:
      // AI yönlendirmeyi yaptı, işi kullanıcı tamamlayıp panelden işaretleyecek.
      const activePlan = activePlanTaskRef.current;
      if (activePlan && (planChainActionOkRef.current || activePlan.completeAs === 'needsReview')) {
        await finishPlanTask(
          activePlan.completeAs,
          (result.reply || '').slice(0, 500),
          result.conversationId
        );
      }
      return;
    }

    // Eylem çağrısı: kullanıcı mesajı başına zincir üst sınırı (sonsuz döngü emniyeti;
    // backend de kendi tarafında tools'u kapatarak sınırlar). Toplu görevler
    // ("10 ürünü güncelle" gibi) sığabilsin diye 30 — backend sınırı ≈20 eylem.
    actionChainRef.current += 1;
    if (actionChainRef.current > 30) {
      autoApproveRef.current = false;
      setMessages((prev) => [
        ...prev,
        { id: `a_${Date.now()}`, role: 'assistant', content: t('aiChat.agentChainLimit'), isError: true },
      ]);
      return;
    }

    const localId = `act_${action.messageId}_${Date.now()}`;
    // read her zaman onaysız; write yalnızca "Tümünü Onayla" aktifse onaysız çalışır
    const autoRun = action.risk === 'read' || autoApproveRef.current;
    setMessages((prev) => [
      ...prev,
      {
        id: localId,
        role: 'assistant',
        content: result.reply,
        actionCall: action,
        actionStatus: autoRun ? 'running' : 'pending',
      },
    ]);

    // Kullanıcı bu turda Durdur'a bastıysa yeni eylem başlatılmaz
    if (stopRequestedRef.current) {
      applyStopCleanup();
      return;
    }

    if (autoRun) {
      await runAction(localId, action, result.conversationId);
    }
  };

  /** Eylemi kullanıcının kendi oturumuyla yürütür ve sonucu backend'e bildirir (devam turu). */
  const runAction = async (localId: string, action: AiChatActionCallDto, convId: number) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === localId ? { ...m, actionStatus: 'running' as const } : m))
    );

    const executor = getActionExecutor(action.name);
    let payload: Record<string, any>;
    if (!executor) {
      payload = { success: false, error: 'Bu eylem bu uygulama sürümünde desteklenmiyor.' };
    } else {
      try {
        payload = await executor.execute(action.args, {
          navigate,
          // Yazma eylemlerinin payload'ları için multi-tenant + denetim bilgisi
          companyId: userDetails?.companyId,
          userEmail: userDetails?.email || currentUser?.email || undefined,
          // AI plan eylemleri (createAiPlan vb.) plan sahipliği için uid ister
          userId: userId || undefined,
        });
      } catch (err: any) {
        console.error('[AiChatWindow] eylem yürütme hatası:', err);
        payload = { success: false, error: err?.message || 'Eylem yürütülemedi.' };
      }
    }

    const ok = payload.success !== false;
    // Plan görevi takibi: zincirde en az bir eylem başarıyla çalıştı → görev "iş yaptı" sayılır
    if (ok && activePlanTaskRef.current) planChainActionOkRef.current = true;

    // completeAiPlanTask: AI aktif görevi bu eylemle kendisi işaretledi (ör. iş zaten
    // yapılmıştı) — yerel görev kilidi de burada çözülür, yoksa activePlanTaskRef asılı
    // kalır ve "tümünü yap" dahil hiçbir yeni görev başlatılamaz. Durum sunucuya
    // executor'da yazıldığı için finishPlanTask çağrılmaz (çift yazma olmasın).
    if (ok && action.name === 'completeAiPlanTask') {
      const active = activePlanTaskRef.current;
      const markedTaskId = Number((action.args as any)?.taskId);
      const markedStatus = String((action.args as any)?.status || 'done');
      if (active && active.taskId === markedTaskId) {
        activePlanTaskRef.current = null;
        setActivePlanTaskId(null);
        // Görevler arası süreklilik: plana bağlı konuşma kaydedilir (fire-and-forget)
        aiPlanService.updatePlan(active.planId, userId, { conversationId: convId }).catch(() => {});
        if (runAllRef.current === active.planId) {
          if (markedStatus === 'done') setRunAllTick((k) => k + 1);
          else runAllRef.current = null;
        }
      }
      setPlanRefreshKey((k) => k + 1);
    }
    setMessages((prev) =>
      prev.map((m) =>
        m.id === localId ? { ...m, actionStatus: (ok ? 'done' : 'failed') as ChatMessage['actionStatus'] } : m
      )
    );

    await submitActionResult(action, payload, convId);
  };

  /** Onay kartında "Vazgeç": eylem çalıştırılmaz, iptal bilgisi AI'ya iletilir. */
  const cancelAction = async (localId: string, action: AiChatActionCallDto, convId: number) => {
    autoApproveRef.current = false; // kullanıcı reddetti — toplu onay modu da kapanır
    // Plan görevi sırasında red: görev "kontrol edin" durumuna alınır (panelde elle karar verilir)
    if (activePlanTaskRef.current) void finishPlanTask('needsReview', undefined, convId);
    setMessages((prev) =>
      prev.map((m) => (m.id === localId ? { ...m, actionStatus: 'cancelled' as const } : m))
    );
    await submitActionResult(
      action,
      { cancelled: true, message: 'Kullanıcı işlemi onaylamadı.' },
      convId
    );
  };

  /**
   * Yürütme/iptal sonucunu backend'e gönderir; dönen devam turu aynı akışla işlenir.
   * Gönderim geçici hatayla düşerse eylem ZATEN yürütülmüş olduğundan yalnızca sonuç
   * bildirimi tekrarlanır ("Devam Et") — eylem ikinci kez ÇALIŞTIRILMAZ.
   */
  const submitActionResult = async (
    action: AiChatActionCallDto,
    payload: Record<string, any>,
    convId: number
  ) => {
    const attempt = async (): Promise<void> => {
      // Durdur'a basıldıysa devam turu hiç başlatılmaz (eylem zaten yürütüldü)
      if (stopRequestedRef.current) {
        applyStopCleanup();
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setSending(true);
      try {
        const result = await aiChatService.sendAiChatActionResult({
          conversationId: convId,
          actionName: action.name,
          result: payload,
          userId,
          branches: branchList,
          // Zincir hangi modda başladıysa devam turu da o modla yapılır
          // (plan modunda plan system prompt'u korunur)
          mode: activeModeRef.current,
          model: chatModel,
        });
        await processSendResult(result, attempt);
      } catch (err) {
        if (isAbortError(err) || stopRequestedRef.current) {
          applyStopCleanup();
        } else {
          console.error('[AiChatWindow] eylem sonucu gönderilemedi:', err);
          pushFailure('aiChat.error', attempt);
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setSending(false);
      }
    };
    await attempt();
  };

  /**
   * Mesaj gönderiminin ortak gövdesi — hem kullanıcı girişi (handleSend) hem plan
   * görev çalıştırıcısı bunu kullanır. conversationId state'i sayesinde ardışık plan
   * görevleri aynı konuşmada devam eder. opts.mode/conversationId: state güncellemeleri
   * asenkron olduğundan plan çalıştırıcı değerleri doğrudan geçirir (stale closure önlemi).
   */
  const sendMessage = async (
    content: string,
    opts?: {
      mode?: 'ask' | 'agent' | 'plan';
      conversationId?: number;
      /** "Tümünü yap" akışı: yazma eylemleri onay kartı beklemeden yürütülür. */
      autoApprove?: boolean;
      /** Durdurulursa mesaj giriş kutusuna geri konur (yalnızca elle yazılan mesajlar). */
      restoreOnStop?: boolean;
      /** Otomatik üretilen komutlar (plan görevleri): chat'e kullanıcı balonu olarak YAZILMAZ, yalnızca AI'ya gider. */
      hidden?: boolean;
    }
  ) => {
    const trimmed = content.trim();
    if (!trimmed || sending || !userId) return;

    // Paketi olmayan şirket: API'ye gitmeden paket tanıtım mesajı göster
    if (!packageActive) {
      setMessages((prev) => [
        ...prev,
        { id: `u_${Date.now()}`, role: 'user', content: trimmed },
        { id: `a_${Date.now()}`, role: 'assistant', content: t('aiChat.upsellMessage'), isUpsell: true },
      ]);
      return;
    }

    if (quotaExceeded) return;

    actionChainRef.current = 0; // yeni kullanıcı mesajı — eylem zinciri sayacı sıfırlanır
    // Toplu onay yalnızca verildiği görev için geçerlidir; "Tümünü yap" akışı ise
    // görev başından itibaren onaysız yürütme ister.
    autoApproveRef.current = opts?.autoApprove === true;
    stopRequestedRef.current = false;
    retryOpRef.current = null;
    const userMsgId = `u_${Date.now()}`;
    setMessages((prev) => {
      const cleared = prev.map((m) => (m.canRetry ? { ...m, canRetry: false } : m));
      // hidden: otomatik görev komutu balon olarak gösterilmez (panel zaten görevi gösteriyor)
      return opts?.hidden ? cleared : [...cleared, { id: userMsgId, role: 'user', content: trimmed }];
    });

    // Mod, şirket bayraklarına göre çözülür: ajan agentEnabled, plan planEnabled ister;
    // bayrak kapalıysa Soru moduna düşülür. Çözülen mod, eylem zincirinin devam
    // turlarında da kullanılmak üzere ref'e yazılır.
    const requestedMode = opts?.mode ?? chatMode;
    const resolvedMode: 'ask' | 'agent' | 'plan' =
      requestedMode === 'agent' && agentEnabled
        ? 'agent'
        : requestedMode === 'plan' && planEnabled
          ? 'plan'
          : 'ask';
    activeModeRef.current = resolvedMode;

    // conversationId gönderim anında yakalanır; ilk denemede başarısız olursa
    // "Devam Et" aynı isteği aynen tekrarlar (backend başarısız turda hiçbir şey kaydetmez)
    const payload = {
      text: trimmed,
      conversationId: opts?.conversationId ?? conversationId,
      userId,
      userEmail,
      branches: branchList,
      mode: resolvedMode,
      model: chatModel,
    };
    const attempt = async (): Promise<void> => {
      const controller = new AbortController();
      abortRef.current = controller;
      setSending(true);
      try {
        const result = await aiChatService.sendAiChatMessage({ ...payload, signal: controller.signal });
        await processSendResult(result, attempt);
      } catch (err) {
        if (isAbortError(err) || stopRequestedRef.current) {
          // Durduruldu: kullanıcı mesajı balondan alınıp giriş kutusuna geri konur —
          // düzenlenip yeniden gönderilebilir (Cursor'daki Stop davranışı)
          applyStopCleanup(
            opts?.restoreOnStop ? userMsgId : undefined,
            opts?.restoreOnStop ? trimmed : undefined
          );
        } else {
          console.error('[AiChatWindow] mesaj gönderilemedi:', err);
          pushFailure('aiChat.error', attempt);
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setSending(false);
      }
    };
    await attempt();
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending || !userId) return;
    if (packageActive && quotaExceeded) return;
    // Elle mesaj, plan görevi cevabı DEĞİLSE "tümünü yap" akışını sonlandırır
    // (görev sırasında AI'nın sorusuna verilen cevaplar akışı bozmaz)
    if (!activePlanTaskRef.current) runAllRef.current = null;
    setText('');
    await sendMessage(trimmed, { restoreOnStop: true });
  };

  /**
   * Plan görevini ajan sohbetinde başlatır: görev 'running' işaretlenir, chat ajan
   * moduna alınır ve bağlam enjeksiyonlu komut gönderilir. continueTask=true →
   * yarım kalan/başarısız görev "kaldığın yerden tamamla" komutuyla sürdürülür.
   */
  const handleRunPlanTask = async (
    plan: AiPlanDetail,
    task: AiPlanTaskDto,
    continueTask: boolean,
    viaRunAll = false
  ) => {
    if (!agentEnabled || sending || !userId) return;
    if (activePlanTaskRef.current) return; // aynı anda tek görev çalışır

    // Panelden tekil "Şimdi yap" tıklaması olası bir "tümünü yap" kalıntısını temizler
    if (!viaRunAll) runAllRef.current = null;

    setChatMode('agent');
    // Plana bağlı önceki konuşma varsa ve pencere boş sohbetteyse oradan devam edilir
    const convId = conversationId ?? plan.conversationId ?? undefined;
    if (!conversationId && plan.conversationId) setConversationId(plan.conversationId);

    activePlanTaskRef.current = {
      planId: plan.id,
      taskId: task.id,
      completeAs: isManualCompletionTask(task) ? 'needsReview' : 'done',
    };
    setActivePlanTaskId(task.id);
    planChainActionOkRef.current = false;

    try {
      await aiPlanService.updateTask(task.id, userId, 'running');
    } catch (err) {
      console.error('[AiChatWindow] plan görevi running yazılamadı:', err);
    }
    setPlanRefreshKey((k) => k + 1);

    const prompt = continueTask ? buildContinueTaskPrompt(task, plan) : buildTaskPrompt(task, plan);
    await sendMessage(prompt, {
      mode: 'agent',
      conversationId: convId,
      // "Tümünü yap": tek onayla tüm görevler — yazma eylemleri onay kartı beklemez
      autoApprove: runAllRef.current === plan.id,
      // Otomatik üretilen görev komutu chat'te gösterilmez (panel görevi zaten gösteriyor)
      hidden: true,
    });
  };

  /**
   * "Tümünü yap" akışında sıradaki görevi başlatır: done olmayan ilk görev alınır.
   * Akış yalnızca ELLE TAMAMLANAN ([ELLE-TAMAMLANIR]) bir needsReview görevinde veya
   * görev kalmadığında biter — durdurma/model değiştirme gibi nedenlerle needsReview
   * kalan normal görevler "kaldığın yerden devam et" komutuyla sürdürülür
   * (failed/yarım kalmış görevler gibi).
   */
  const startNextRunAllTask = async (planId: number) => {
    try {
      const plan = await aiPlanService.getPlan(planId, userId);
      const tasks = [...plan.tasks].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
      const next = tasks.find((task) => task.status !== 'done');
      if (!next || (next.status === 'needsReview' && isManualCompletionTask(next))) {
        runAllRef.current = null;
        return;
      }
      await handleRunPlanTask(plan, next, next.status !== 'pending', true);
    } catch (err) {
      console.error('[AiChatWindow] tümünü yap: sıradaki görev başlatılamadı:', err);
      runAllRef.current = null;
    }
  };

  /**
   * Panel "Tümünü yap" butonu: kalan görevler sırayla, eylem onayları otomatik çalışır.
   * Chat plan modundaysa anında Ajan moduna geçilir (Cursor davranışı) — görevler
   * zaten ajan modunda yürür, mod seçici de bunu hemen yansıtır.
   */
  const handleRunAllPlanTasks = async (plan: AiPlanDetail) => {
    if (!agentEnabled || sending || !userId || activePlanTaskRef.current) return;
    setChatMode('agent');
    runAllRef.current = plan.id;
    await startNextRunAllTask(plan.id);
  };

  // "Tümünü yap" zinciri: görev 'done' bitince (runAllTick artar) eylem zinciri
  // tamamen çözülüp sending=false olduğunda sıradaki görev başlatılır.
  useEffect(() => {
    if (runAllTick === 0 || sending) return;
    const planId = runAllRef.current;
    if (!planId || activePlanTaskRef.current) return;
    void startNextRunAllTask(planId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runAllTick, sending]);

  const handleNewConversation = () => {
    setConversationId(undefined);
    setMessages([]);
    setShowHistory(false);
  };

  const handleOpenHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      setConversations(await aiChatService.getAiChatConversations(userId));
    } catch (err) {
      console.error('[AiChatWindow] konuşmalar yüklenemedi:', err);
      setConversations([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleLoadConversation = async (id: number) => {
    setHistoryLoading(true);
    try {
      const serverMessages = await aiChatService.getAiChatConversationMessages(id, userId);
      setConversationId(id);
      setMessages(
        serverMessages
          // functionResponse teknik ara mesajdır — kullanıcıya gösterilmez;
          // otomatik üretilen plan görev komutları da gizlenir (canlı gönderimle tutarlı)
          .filter((m) => m.kind !== 'functionResponse' && !(m.role === 'user' && isPlanTaskPrompt(m.content || '')))
          .map((m) => ({
            id: `s_${m.id}`,
            role: m.role,
            content:
              m.kind === 'functionCall' && !m.content
                ? t('aiChat.agentActionRan', { name: m.actionName || '' })
                : m.content,
            reportSpec: m.reportSpec,
          }))
      );
      setShowHistory(false);
    } catch (err) {
      console.error('[AiChatWindow] konuşma yüklenemedi:', err);
      setMessages([
        { id: `e_${Date.now()}`, role: 'assistant', content: t('aiChat.loadError'), isError: true },
      ]);
      setShowHistory(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAddReport = useCallback((message: ChatMessage) => {
    if (!message.reportSpec) return;
    upsertCustomReport(toReportSpec(message.reportSpec));
    // Dashboard açıksa listeyi canlı yenilesin
    window.dispatchEvent(new CustomEvent('gts:customReportsChanged'));
    setMessages((prev) =>
      prev.map((m) => (m.id === message.id ? { ...m, reportAdded: true } : m))
    );
  }, []);

  return (
    <>
    {/* Plan paneli — chat Paper'ının KARDEŞİ (soldaki resize tutamacını bozmaz) */}
    {planEnabled && packageActive && (
      <AiPlanPanel
        open={planPanelOpen}
        onClose={() => setPlanPanelOpen(false)}
        isMobile={isMobile}
        chatWidth={width}
        agentEnabled={agentEnabled}
        busy={sending}
        userId={userId}
        activeTaskId={activePlanTaskId}
        refreshKey={planRefreshKey}
        onRunTask={handleRunPlanTask}
        onRunAll={handleRunAllPlanTasks}
      />
    )}
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        right: isMobile ? 0 : 16,
        bottom: isMobile ? 0 : 88,
        width: isMobile ? '100%' : width,
        // Masaüstünde yükseklik içeriğe göre büyür: alt kenar sabit olduğundan
        // pencere doldukça yukarı doğru uzar, tavana yaklaşınca içeride kaydırma başlar.
        height: isMobile ? '100%' : 'auto',
        minHeight: isMobile ? '100%' : 560,
        maxHeight: isMobile ? '100%' : 'calc(100vh - 120px)',
        zIndex: 2100,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: isMobile ? 0 : 3,
        overflow: 'hidden',
        // Sürükleme sırasında metin seçimini ve geçiş animasyonunu kapat
        userSelect: resizing ? 'none' : undefined,
        transition: resizing ? 'none' : 'width 0.15s ease',
      }}
    >
      {/* Sol kenar: sürükleyerek genişletme tutamacı (masaüstü) */}
      {!isMobile && (
        <Box
          onPointerDown={handleResizeStart}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'ew-resize',
            zIndex: 1,
            // Üzerine gelince/sürüklerken ince mavi çizgiyle belli olur
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              bgcolor: resizing ? 'primary.main' : 'transparent',
              transition: 'background-color 0.15s',
            },
            '&:hover::after': { bgcolor: 'primary.main' },
          }}
        />
      )}
      {/* Başlık — üst satır: ad; alt satır: aksiyon butonları */}
      <Box
        sx={{
          px: 2,
          pt: 1.25,
          pb: 0.75,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }} noWrap>
            {t('aiChat.title')}
          </Typography>
          {/* Rehber penceresi ve ipucu sohbet penceresinin (zIndex 2100) üstünde açılsın */}
          <ThemeProvider theme={raiseGuideLayers}>
            <GuideButton guideKey="aichat.window" size="small" sx={{ color: 'inherit' }} />
          </ThemeProvider>
          {/* Kapatma yalnızca mobilde: tam ekran pencere alttaki FAB'ı örter;
              masaüstünde pencere FAB ile kapatılır (bottom: 88 > FAB) */}
          {isMobile && (
            <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Tooltip title={t('aiChat.packages')}>
            <IconButton size="small" onClick={() => setPackagesOpen(true)} sx={{ color: 'inherit' }}>
              <WorkspacePremiumIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {packageActive && (
            <>
              {/* Plan Modu paneli (AI Pro) — yalnızca planEnabled şirketlerde görünür */}
              {planEnabled && (
                <Tooltip title={t('aiChat.planPanelTooltip')}>
                  <IconButton
                    size="small"
                    onClick={() => setPlanPanelOpen((prev) => !prev)}
                    sx={{ color: 'inherit', bgcolor: planPanelOpen ? 'rgba(255,255,255,0.18)' : undefined }}
                  >
                    <ChecklistIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('aiChat.newConversation')}>
                <IconButton size="small" onClick={handleNewConversation} sx={{ color: 'inherit' }}>
                  <AddCommentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('aiChat.history')}>
                <IconButton size="small" onClick={handleOpenHistory} sx={{ color: 'inherit' }}>
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {showHistory ? (
        /* Konuşma geçmişi görünümü */
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Box sx={{ px: 1, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setShowHistory(false)}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {t('aiChat.backToChat')}
            </Typography>
          </Box>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : conversations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              {t('aiChat.emptyHistory')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {conversations.map((c) => (
                <ListItemButton key={c.id} onClick={() => handleLoadConversation(c.id)}>
                  <ListItemText
                    primary={c.title || `#${c.id}`}
                    secondary={new Date(c.updatedAt).toLocaleString()}
                    primaryTypographyProps={{ noWrap: true, fontSize: 14 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      ) : (
        /* Mesaj listesi */
        <Box
          ref={listRef}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            px: 1.5,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            bgcolor: 'grey.50',
          }}
        >
          {messages.length === 0 && (
            <Alert severity="info" icon={<AutoAwesomeIcon fontSize="small" />} sx={{ fontSize: 13 }}>
              {t('aiChat.welcome')}
            </Alert>
          )}

          {messages.map((m) => (
            <Box
              key={m.id}
              sx={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '88%',
              }}
            >
              {m.isUpsell ? (
                /* Paket tanıtımı: uyarı + paketleri gör + iletişim sayfası yönlendirmesi */
                <Alert
                  severity="info"
                  icon={<WorkspacePremiumIcon fontSize="small" />}
                  sx={{ borderRadius: 2, fontSize: 13, '& .MuiAlert-message': { whiteSpace: 'pre-wrap' } }}
                >
                  {m.content}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<WorkspacePremiumIcon />}
                      onClick={() => setPackagesOpen(true)}
                    >
                      {t('aiChat.viewPackages')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.open('#/contact', '_blank')}
                    >
                      {t('aiChat.goToContact')}
                    </Button>
                  </Box>
                </Alert>
              ) : m.isError ? (
                /* Sorun bildirimleri kırmızı hata yerine yumuşak sarı UYARI olarak gösterilir */
                <Alert
                  severity="warning"
                  sx={{ borderRadius: 2, fontSize: 13, py: 0.5, '& .MuiAlert-message': { whiteSpace: 'pre-wrap' } }}
                >
                  {m.content}
                  {/* Geçici hata (yoğunluk vb.): yarım kalan görev kaldığı yerden sürdürülür */}
                  {m.canRetry && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<PlayArrowIcon />}
                        disabled={sending}
                        onClick={() => handleRetry(m.id)}
                      >
                        {t('aiChat.continueTask')}
                      </Button>
                    </Box>
                  )}
                </Alert>
              ) : m.content ? (
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: m.role === 'user' ? 'primary.main' : 'background.paper',
                    color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    border: m.role === 'assistant' ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  {m.role === 'assistant' ? (
                    renderAssistantContent(m.content, handleImageClick)
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.content}
                    </Typography>
                  )}
                </Paper>
              ) : null}

              {/* Ajan eylem kartı: read otomatik yürütülür (durum göstergesi),
                  write onay bekler (Onayla / Vazgeç) */}
              {m.actionCall && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: m.content ? 1 : 0,
                    p: 1.25,
                    borderRadius: 2,
                    borderColor:
                      m.actionStatus === 'pending'
                        ? 'warning.main'
                        : m.actionStatus === 'failed'
                          ? 'error.main'
                          : 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <BoltIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
                      {getActionExecutor(m.actionCall.name)?.describe(m.actionCall.args) ??
                        m.actionCall.name}
                    </Typography>
                    {m.actionStatus === 'running' && <CircularProgress size={14} />}
                    {m.actionStatus === 'done' && (
                      <Chip size="small" color="success" label={t('aiChat.agentDone')} sx={{ height: 20, fontSize: 11 }} />
                    )}
                    {m.actionStatus === 'cancelled' && (
                      <Chip size="small" label={t('aiChat.agentCancelled')} sx={{ height: 20, fontSize: 11 }} />
                    )}
                    {m.actionStatus === 'failed' && (
                      <Chip size="small" color="error" label={t('aiChat.agentFailed')} sx={{ height: 20, fontSize: 11 }} />
                    )}
                  </Box>

                  {m.actionStatus === 'pending' && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {t('aiChat.agentConfirmText')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<PlayArrowIcon />}
                          disabled={sending}
                          onClick={() => runAction(m.id, m.actionCall!, conversationId ?? 0)}
                        >
                          {t('aiChat.agentApprove')}
                        </Button>
                        {/* Toplu görevlerde tek onay: görev bitene kadar sonraki
                            yazma eylemleri de onay beklemeden çalışır */}
                        <Tooltip title={t('aiChat.agentApproveAllHint')}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<BoltIcon />}
                            disabled={sending}
                            onClick={() => {
                              autoApproveRef.current = true;
                              runAction(m.id, m.actionCall!, conversationId ?? 0);
                            }}
                          >
                            {t('aiChat.agentApproveAll')}
                          </Button>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<BlockIcon />}
                          disabled={sending}
                          onClick={() => cancelAction(m.id, m.actionCall!, conversationId ?? 0)}
                        >
                          {t('aiChat.agentReject')}
                        </Button>
                      </Box>
                    </>
                  )}
                </Paper>
              )}

              {/* Rapor önerisi: önizleme kartı + Dashboard'a ekle */}
              {m.reportSpec && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                      {t('aiChat.reportPreview')}
                    </Typography>
                    {/* AI'nın çözdüğü tarih aralığı (yoksa varsayılan son 30 gün) */}
                    <Chip
                      size="small"
                      icon={<CalendarMonthIcon sx={{ fontSize: 13 }} />}
                      label={formatSpecDateRange(m.reportSpec) ?? t('aiChat.defaultRange')}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                    <Tooltip title={t('aiChat.expandReport')}>
                      <IconButton size="small" onClick={() => setExpandedReport(m.reportSpec!)}>
                        <OpenInFullIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ height: m.reportSpec.visual === 'kpi' ? 130 : 260, mt: 0.5 }}>
                    <DynamicReportCard
                      spec={toReportSpec(m.reportSpec)}
                      startDate={(resolveSpecDateRange(m.reportSpec) ?? previewRange).startDate}
                      endDate={(resolveSpecDateRange(m.reportSpec) ?? previewRange).endDate}
                      branches={branches || []}
                    />
                  </Box>
                  {m.reportAdded ? (
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircleIcon />}
                      label={t('aiChat.addedToDashboard')}
                      sx={{ mt: 1, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddchartIcon />}
                      onClick={() => handleAddReport(m)}
                      sx={{ mt: 1 }}
                    >
                      {t('aiChat.addToDashboard')}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          ))}

          {sending && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                {t('aiChat.thinking')}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Giriş alanı */}
      {!showHistory && (
        <Box sx={{ p: 1.25, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          {packageActive && quotaExceeded && (
            <Alert severity="warning" sx={{ mb: 1, fontSize: 12, py: 0 }}>
              {t('aiChat.quotaExceeded')}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              // Cursor tarzı: yazdıkça 8 satıra kadar büyür, sonrası içeride kayar
              maxRows={8}
              size="small"
              placeholder={t('aiChat.placeholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              inputProps={{ maxLength: 2000 }}
              disabled={sending || (packageActive && quotaExceeded)}
            />
            {sending ? (
              <Tooltip title={t('aiChat.stop')}>
                <IconButton color="error" onClick={handleStop}>
                  <StopCircleIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t('aiChat.send')}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={(packageActive && quotaExceeded) || !text.trim()}
                  >
                    <SendIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* Cursor tarzı mod + model seçici (sağ alt): iki kompakt açılır menü */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.75 }}>
            {/* Mod seçici: Soru / Ajan */}
            <Button
              size="small"
              onClick={(e) => setModeMenuAnchor(e.currentTarget)}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                textTransform: 'none',
                fontSize: 11,
                py: 0.25,
                px: 1,
                minWidth: 0,
                color: 'text.secondary',
                '& .MuiButton-endIcon': { ml: 0.25 },
              }}
            >
              {chatMode === 'agent' ? (
                <SmartToyIcon sx={{ fontSize: 14, mr: 0.5 }} />
              ) : chatMode === 'plan' ? (
                <ChecklistIcon sx={{ fontSize: 14, mr: 0.5 }} />
              ) : (
                <QuestionAnswerIcon sx={{ fontSize: 14, mr: 0.5 }} />
              )}
              {chatMode === 'agent'
                ? t('aiChat.modeAgent')
                : chatMode === 'plan'
                  ? t('aiChat.modePlan')
                  : t('aiChat.modeAsk')}
            </Button>
            <Menu
              anchorEl={modeMenuAnchor}
              open={Boolean(modeMenuAnchor)}
              onClose={() => setModeMenuAnchor(null)}
              sx={{ zIndex: 2300 }}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <MenuItem
                selected={chatMode === 'ask'}
                onClick={() => {
                  setChatMode('ask');
                  setModeMenuAnchor(null);
                }}
                sx={{ fontSize: 13, minWidth: 170 }}
              >
                <ListItemIcon>
                  <QuestionAnswerIcon sx={{ fontSize: 16 }} />
                </ListItemIcon>
                {t('aiChat.modeAsk')}
                {chatMode === 'ask' && <CheckIcon sx={{ fontSize: 15, ml: 'auto' }} />}
              </MenuItem>
              {agentEnabled ? (
                <MenuItem
                  selected={chatMode === 'agent'}
                  onClick={() => {
                    setChatMode('agent');
                    setModeMenuAnchor(null);
                  }}
                  sx={{ fontSize: 13, minWidth: 170 }}
                >
                  <ListItemIcon>
                    <SmartToyIcon sx={{ fontSize: 16 }} />
                  </ListItemIcon>
                  {t('aiChat.modeAgent')}
                  {chatMode === 'agent' && <CheckIcon sx={{ fontSize: 15, ml: 'auto' }} />}
                </MenuItem>
              ) : (
                <Tooltip title={t('aiChat.modeAgentProRequired')} placement="left">
                  <span>
                    <MenuItem disabled sx={{ fontSize: 13, minWidth: 170 }}>
                      <ListItemIcon>
                        <SmartToyIcon sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      {t('aiChat.modeAgent')}
                      <Chip label={t('aiChat.comingSoon')} size="small" sx={{ height: 14, fontSize: 9, ml: 'auto' }} />
                    </MenuItem>
                  </span>
                </Tooltip>
              )}
              {/* Plan modu: AI kullanıcıyla konuşarak görev planı kurar (AI Pro / planEnabled) */}
              {planEnabled ? (
                <MenuItem
                  selected={chatMode === 'plan'}
                  onClick={() => {
                    setChatMode('plan');
                    setPlanPanelOpen(true); // plan modunda panel birlikte açılır
                    setModeMenuAnchor(null);
                  }}
                  sx={{ fontSize: 13, minWidth: 170 }}
                >
                  <ListItemIcon>
                    <ChecklistIcon sx={{ fontSize: 16 }} />
                  </ListItemIcon>
                  {t('aiChat.modePlan')}
                  {chatMode === 'plan' && <CheckIcon sx={{ fontSize: 15, ml: 'auto' }} />}
                </MenuItem>
              ) : (
                <Tooltip title={t('aiChat.modePlanProRequired')} placement="left">
                  <span>
                    <MenuItem disabled sx={{ fontSize: 13, minWidth: 170 }}>
                      <ListItemIcon>
                        <ChecklistIcon sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      {t('aiChat.modePlan')}
                      <Chip label={t('aiChat.comingSoon')} size="small" sx={{ height: 14, fontSize: 9, ml: 'auto' }} />
                    </MenuItem>
                  </span>
                </Tooltip>
              )}
            </Menu>

            {/* Model seçici: aktifler + Çok Yakında listesi */}
            <Button
              size="small"
              onClick={(e) => setModelMenuAnchor(e.currentTarget)}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                textTransform: 'none',
                fontSize: 11,
                py: 0.25,
                px: 1,
                minWidth: 0,
                color: 'text.secondary',
                '& .MuiButton-endIcon': { ml: 0.25 },
              }}
            >
              {SELECTABLE_MODELS.find((m) => m.id === chatModel)?.label ?? SELECTABLE_MODELS[0].label}
            </Button>
            <Menu
              anchorEl={modelMenuAnchor}
              open={Boolean(modelMenuAnchor)}
              onClose={() => setModelMenuAnchor(null)}
              sx={{ zIndex: 2300 }}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              {SELECTABLE_MODELS.map((m) => {
                const locked = Boolean(m.proOnly) && !agentEnabled;
                const item = (
                  <MenuItem
                    key={m.id}
                    selected={m.id === chatModel}
                    disabled={locked}
                    onClick={() => !locked && selectModel(m.id)}
                    sx={{ fontSize: 13, minWidth: 190, gap: 1 }}
                  >
                    {m.label}
                    {m.quotaNote && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                        {m.quotaNote}
                      </Typography>
                    )}
                    {locked ? (
                      <Chip label="AI Pro" size="small" color="warning" sx={{ height: 14, fontSize: 9, ml: 'auto' }} />
                    ) : (
                      m.id === chatModel && <CheckIcon sx={{ fontSize: 15, ml: 'auto' }} />
                    )}
                  </MenuItem>
                );
                return locked ? (
                  <Tooltip key={m.id} title={t('aiChat.modelProRequired')} placement="left">
                    <span>{item}</span>
                  </Tooltip>
                ) : (
                  item
                );
              })}
              <Divider />
              <Typography
                variant="caption"
                sx={{ px: 2, py: 0.5, display: 'block', color: 'text.disabled', fontWeight: 700 }}
              >
                {t('aiChat.comingSoon')}
              </Typography>
              {COMING_SOON_MODELS.map((label) => (
                <MenuItem key={label} disabled sx={{ fontSize: 13, minWidth: 190, opacity: 0.6 }}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
      )}

      {/* Paketler modalı: AI Standart / AI Pro tanıtımı — satın alma henüz kapalı (Çok Yakında) */}
      <Dialog
        open={packagesOpen}
        onClose={() => setPackagesOpen(false)}
        maxWidth="sm"
        fullWidth
        // Chat penceresi zIndex 2100 — modal her zaman onun üstünde
        sx={{ zIndex: 2300 }}
      >
        <Box sx={{ p: 2.5, position: 'relative' }}>
          <IconButton
            size="small"
            onClick={() => setPackagesOpen(false)}
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <WorkspacePremiumIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              {t('aiChat.packagesTitle')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            {/* AI STANDART */}
            <Paper
              variant="outlined"
              sx={{ flex: 1, p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  AI STANDART
                </Typography>
                <Chip size="small" color="warning" label={t('aiChat.comingSoon')} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                • {t('aiChat.pkgStdFeature1')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {t('aiChat.pkgStdFeature2')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {t('aiChat.pkgStdFeature3')}
              </Typography>
            </Paper>

            {/* AI PRO */}
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                borderColor: 'primary.main',
                borderWidth: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary">
                  AI PRO
                </Typography>
                <Chip size="small" color="warning" label={t('aiChat.comingSoon')} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                • {t('aiChat.pkgProFeature1')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {t('aiChat.pkgProFeature2')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {t('aiChat.pkgProFeature3')}
              </Typography>
            </Paper>
          </Box>

          {/* Satın alma henüz kapalı — telefonla iletişim */}
          <Alert severity="info" icon={<LocalPhoneIcon fontSize="small" />} sx={{ mt: 2, fontSize: 13 }}>
            {t('aiChat.callUs')}{' '}
            <Box
              component="a"
              href={`tel:${SALES_PHONE_TEL}`}
              sx={{ fontWeight: 700, color: 'inherit', whiteSpace: 'nowrap' }}
            >
              {SALES_PHONE_DISPLAY}
            </Box>
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={() => window.open('#/contact', '_blank')}>
              {t('aiChat.goToContact')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Rapor büyütme modalı: chat'teki kart aynı tarih aralığıyla büyük boy çizilir */}
      <Dialog
        open={expandedReport !== null}
        onClose={() => setExpandedReport(null)}
        maxWidth="lg"
        fullWidth
        // Chat penceresi zIndex 2100 — modal her zaman onun üstünde
        sx={{ zIndex: 2300 }}
      >
        {expandedReport && (
          <Box sx={{ p: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pr: 4 }}>
              <Chip
                size="small"
                icon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
                label={formatSpecDateRange(expandedReport) ?? t('aiChat.defaultRange')}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => setExpandedReport(null)}
              sx={{ position: 'absolute', top: 12, right: 12 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ height: expandedReport.visual === 'kpi' ? 200 : '70vh', minHeight: 200 }}>
              <DynamicReportCard
                spec={toReportSpec(expandedReport)}
                startDate={(resolveSpecDateRange(expandedReport) ?? previewRange).startDate}
                endDate={(resolveSpecDateRange(expandedReport) ?? previewRange).endDate}
                branches={branches || []}
              />
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Görsel önizleme modalı (yeni sekme yerine uygulama içinde açılır) */}
      <Dialog
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        // Chat penceresi zIndex 2100 — önizleme her zaman onun üstünde kalmalı
        sx={{ zIndex: 2300 }}
        PaperProps={{
          sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' },
        }}
      >
        {previewImage && (
          <Box sx={{ position: 'relative' }} onClick={() => setPreviewImage(null)}>
            <IconButton
              size="small"
              onClick={() => setPreviewImage(null)}
              sx={{
                position: 'absolute',
                top: -16,
                right: -16,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={previewImage.src}
              alt={previewImage.alt}
              sx={{
                display: 'block',
                maxWidth: '90vw',
                maxHeight: '85vh',
                borderRadius: 2,
                boxShadow: 8,
              }}
            />
          </Box>
        )}
      </Dialog>
    </Paper>
    </>
  );
};

export default AiChatWindow;
